import React, { useState, useEffect } from 'react';

/**
 * CurationPipelineCard - Visual funnel showing lead curation status
 * 
 * Shows AI-powered lead lifecycle:
 * - Pending Triage → AI analyzing
 * - Needs Clarification → Waiting for user reply
 * - Needs Admin Review → Flagged for human
 * - Ready for Distribution → Complete, queued for digest
 * - Distributed → Sent to sales team
 */
const CurationPipelineCard = ({ backendUrl }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    loadPipelineStats();
  }, [backendUrl]);

  const loadPipelineStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/curation/pipeline-stats`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.pipeline);
      } else {
        setError(data.error || 'Failed to load stats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Status config with colors and icons
  const statusConfig = {
    pending_triage: { 
      label: 'Pending Triage', 
      icon: '🔍', 
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      description: 'AI analyzing'
    },
    needs_user_clarification: { 
      label: 'Awaiting Reply', 
      icon: '📧', 
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20',
      description: 'Sent clarification email'
    },
    needs_admin_review: { 
      label: 'Admin Review', 
      icon: '👤', 
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/20',
      description: 'Flagged for human'
    },
    ready_for_distribution: { 
      label: 'Ready to Send', 
      icon: '✅', 
      color: 'text-[var(--pbfs-green)]',
      bgColor: 'bg-[var(--pbfs-green)]/20',
      description: 'Queued for digest'
    },
    distributed: { 
      label: 'Distributed', 
      icon: '📤', 
      color: 'text-white/60',
      bgColor: 'bg-white/10',
      description: 'Sent to sales'
    },
    archived: { 
      label: 'Archived', 
      icon: '📁', 
      color: 'text-white/40',
      bgColor: 'bg-white/5',
      description: 'Closed'
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-6 h-6 bg-white/20 rounded"></div>
          <div className="h-5 bg-white/20 rounded w-40"></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-20 bg-white/10 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    // Check if it's a rate limit error
    const isRateLimit = error.includes('429') || error.includes('16500') || error.includes('TooManyRequests');
    
    return (
      <div className={`${isRateLimit ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30'} border rounded-xl p-6`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{isRateLimit ? '⏳' : '⚠️'}</span>
          <div>
            <div className={`${isRateLimit ? 'text-yellow-400' : 'text-red-400'} font-semibold`}>
              {isRateLimit ? 'Database Busy' : 'Pipeline Stats Unavailable'}
            </div>
            <div className={`${isRateLimit ? 'text-yellow-300/60' : 'text-red-300/60'} text-sm`}>
              {isRateLimit ? 'High traffic - stats will load shortly' : error}
            </div>
            <button 
              onClick={loadPipelineStats}
              className={`${isRateLimit ? 'text-yellow-400 hover:text-yellow-300' : 'text-red-400 hover:text-red-300'} text-sm mt-2 underline`}
            >
              Retry Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate totals
  const actionableCount = 
    (stats?.pending_triage || 0) + 
    (stats?.needs_user_clarification || 0) + 
    (stats?.needs_admin_review || 0);
  
  const totalProcessed = 
    (stats?.ready_for_distribution || 0) + 
    (stats?.distributed || 0);

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div 
        className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h3 className="text-white font-bold">Lead Curation Pipeline</h3>
            <p className="text-white/50 text-sm">AI-powered lead lifecycle</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {actionableCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-orange-500/20 rounded-full">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></span>
              <span className="text-orange-400 font-semibold text-sm">{actionableCount} need action</span>
            </div>
          )}
          <button className="text-white/60 hover:text-white text-xl transition-transform" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </button>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="px-5 pb-4 grid grid-cols-4 gap-3">
        {/* Actionable */}
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-400">{actionableCount}</div>
          <div className="text-orange-300/60 text-xs">Need Action</div>
        </div>
        
        {/* Ready */}
        <div className="bg-[var(--pbfs-green)]/10 border border-[var(--pbfs-green)]/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-[var(--pbfs-green)]">{stats?.ready_for_distribution || 0}</div>
          <div className="text-[var(--pbfs-green)]/60 text-xs">Ready to Send</div>
        </div>
        
        {/* Distributed */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{stats?.distributed || 0}</div>
          <div className="text-white/40 text-xs">Distributed</div>
        </div>
        
        {/* Legacy */}
        {stats?.legacy_no_status > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-400">{stats.legacy_no_status}</div>
            <div className="text-blue-300/60 text-xs">Legacy (no status)</div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-white/10 px-5 py-4 space-y-3 bg-white/[0.02]">
          <div className="text-white/50 text-sm font-semibold mb-2">Detailed Breakdown</div>
          
          {Object.entries(statusConfig).map(([key, config]) => {
            const count = stats?.[key] || 0;
            return (
              <div key={key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 ${config.bgColor} rounded-lg flex items-center justify-center`}>
                    <span>{config.icon}</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${config.color}`}>{config.label}</div>
                    <div className="text-white/40 text-xs">{config.description}</div>
                  </div>
                </div>
                <div className={`text-xl font-bold ${count > 0 ? config.color : 'text-white/20'}`}>
                  {count}
                </div>
              </div>
            );
          })}

          {/* Approval Status */}
          {stats?.approval_status && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-white/50 text-sm font-semibold mb-2">By Approval Status</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-[var(--pbfs-green)]/10 rounded">
                  <div className="text-[var(--pbfs-green)] font-bold">{stats.approval_status.approved || 0}</div>
                  <div className="text-white/40 text-xs">Approved</div>
                </div>
                <div className="text-center p-2 bg-yellow-500/10 rounded">
                  <div className="text-yellow-400 font-bold">{stats.approval_status.pending || 0}</div>
                  <div className="text-white/40 text-xs">Pending</div>
                </div>
                <div className="text-center p-2 bg-red-500/10 rounded">
                  <div className="text-red-400 font-bold">{stats.approval_status.rejected || 0}</div>
                  <div className="text-white/40 text-xs">Rejected</div>
                </div>
              </div>
            </div>
          )}

          {/* Refresh */}
          <div className="pt-2 flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); loadPipelineStats(); }}
              className="text-[var(--pbfs-blue)] hover:text-[var(--pbfs-blue)]/80 text-sm flex items-center gap-1"
            >
              <span>🔄</span> Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurationPipelineCard;

